import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Logo } from '../components/Logo';
import { toast } from 'sonner';
import { Loader2, PawPrint, Briefcase } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function GoogleCallbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [phase, setPhase] = useState('processing'); // processing | pickRole | provider-details | error
  const [profile, setProfile] = useState(null);
  const [pendingToken, setPendingToken] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const finishLogin = async (data) => {
    await login(data.token);
    if (data.role === 'provider') {
      navigate(data.onboardingCompleted === false ? '/onboarding' : '/dashboard', { replace: true });
    } else {
      navigate('/client/dashboard', { replace: true });
    }
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setPhase('error');
      setErrorMsg('No sign-in session found. Please try signing in with Google again.');
      return;
    }
    // Strip session_id from the URL immediately (use-once token)
    window.history.replaceState(null, '', window.location.pathname);
    (async () => {
      try {
        const res = await axios.post(`${API_URL}/api/auth/google/session`, { sessionId: match[1] });
        if (res.data.needsRole) {
          setProfile(res.data.profile);
          setPendingToken(res.data.pendingToken);
          setPhase('pickRole');
        } else {
          await finishLogin(res.data);
        }
      } catch (err) {
        setPhase('error');
        setErrorMsg(err.response?.data?.detail || 'Google sign-in failed. Please try again.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const complete = async (role) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/google/complete`, {
        pendingToken,
        role,
        businessName: role === 'provider' ? businessName : undefined,
        phone: phone || undefined,
      });
      toast.success('Welcome to RanchWorks!');
      await finishLogin(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create your account');
      setSubmitting(false);
    }
  };

  if (phase === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="text-center space-y-3" data-testid="google-processing">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-sm text-muted-foreground">Signing you in with Google…</p>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Sign-in problem</CardTitle>
            <CardDescription data-testid="google-error">{errorMsg}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/login')} data-testid="google-back-to-login">Back to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4 py-8">
      <Card className="max-w-lg w-full" data-testid="google-role-picker">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2"><Logo /></div>
          <CardTitle className="text-xl">Welcome{profile?.name ? `, ${profile.name.split(' ')[0]}` : ''}!</CardTitle>
          <CardDescription>
            You're signed in with Google as <span className="font-medium">{profile?.email}</span>.
            How will you use RanchWorks?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {phase === 'pickRole' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                disabled={submitting}
                onClick={() => complete('client')}
                className="rounded-lg border p-4 text-left hover:border-primary hover:bg-primary/5 transition-colors"
                data-testid="google-role-client"
              >
                <PawPrint className="h-6 w-6 text-primary mb-2" />
                <p className="font-semibold">I need services</p>
                <p className="text-xs text-muted-foreground mt-1">Book farriers, vets, trail rides and more for my animals.</p>
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => setPhase('provider-details')}
                className="rounded-lg border p-4 text-left hover:border-primary hover:bg-primary/5 transition-colors"
                data-testid="google-role-provider"
              >
                <Briefcase className="h-6 w-6 text-primary mb-2" />
                <p className="font-semibold">I provide services</p>
                <p className="text-xs text-muted-foreground mt-1">List my business, manage bookings, clients and invoices.</p>
              </button>
            </div>
          )}
          {phase === 'provider-details' && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="g-business">Business Name *</Label>
                <Input id="g-business" value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Smith Farrier Services" data-testid="google-business-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="g-phone">Phone *</Label>
                <Input id="g-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567" data-testid="google-phone-input" />
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" disabled={submitting} onClick={() => setPhase('pickRole')} data-testid="google-provider-back">
                  Back
                </Button>
                <Button className="flex-1" disabled={submitting || !businessName.trim() || !phone.trim()}
                  onClick={() => complete('provider')} data-testid="google-provider-create">
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Create Provider Account
                </Button>
              </div>
            </div>
          )}
          {submitting && phase === 'pickRole' && (
            <div className="flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
